"use strict";
/**
 * Plan 71 — Questionnaire coverage check.
 *
 * A bundled Questionnaire is only useful if the surrounding CollectorRequest
 * grants permissions on every stream the questionnaire's referenced items live
 * on. When a doctor (or a generator) adds a Questionnaire to a request that
 * doesn't already cover its items, the bundled questionnaire's prefill /
 * answer-related-event flow can't reach the patient's existing typed events.
 *
 * This helper compares a Questionnaire against a CollectorRequest's
 * permissions, surfaces any gaps (per question), and proposes the additional
 * permissions needed. The companion convenience
 * `CollectorRequest#applyQuestionnaireCoverage` mutates the request in place.
 *
 * Scope: permissions only. Sections and customFields are doctor-curated UX
 * surfaces, not correctness constraints — a question's item does NOT need a
 * matching section entry; it only needs a permission so the answer event's
 * `references[]` can be resolved on the doctor's side.
 *
 * Unknown itemRefs (no matching item in the loaded HDS model) are surfaced as
 * `unknownItem: true` rows; the helper never throws on them. Callers should
 * decide whether to block submission or just warn the doctor.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkQuestionnaireCoverage = checkQuestionnaireCoverage;
const HDSModelInitAndSingleton_ts_1 = require("../HDSModel/HDSModelInitAndSingleton.js");
const Questionnaire_ts_1 = require("./Questionnaire.js");
/** Normalize either a `Questionnaire` instance or a raw content payload to content. */
function asQuestionnaireContent(q) {
    if (q instanceof Questionnaire_ts_1.Questionnaire)
        return q.toRequestEventContent();
    return q;
}
/** Normalize either a `CollectorRequest` instance or any object exposing `permissions[]`. */
function asRequest(request) {
    // CollectorRequest exposes `.permissions` as a getter and `.content.permissions`; both work.
    const r = request;
    if (Array.isArray(r.permissions))
        return r;
    const c = request.content;
    if (c && Array.isArray(c.permissions))
        return { permissions: c.permissions };
    return { permissions: [] };
}
/**
 * Compute the coverage report. Read-only — does not mutate either input.
 */
function checkQuestionnaireCoverage(questionnaire, request) {
    const content = asQuestionnaireContent(questionnaire);
    const req = asRequest(request);
    const model = (0, HDSModelInitAndSingleton_ts_1.getModel)();
    const existingByStreamId = {};
    for (const p of req.permissions) {
        if (typeof p?.streamId === 'string')
            existingByStreamId[p.streamId] = { streamId: p.streamId, level: p.level };
    }
    function findCoveringPermission(streamId) {
        if (existingByStreamId[streamId])
            return existingByStreamId[streamId];
        // Walk ancestors via the model — a parent permission covers descendants.
        const ancestors = model.streams.getParentsIds(streamId, false);
        for (const parent of ancestors) {
            if (existingByStreamId[parent])
                return existingByStreamId[parent];
        }
        return null;
    }
    const perQuestion = [];
    const unknownItems = [];
    const missingItemKeysSet = new Set();
    for (const [key, q] of Object.entries(content.questions ?? {})) {
        const itemRef = q.itemRef;
        const itemDef = model.itemsDefs.forKey(itemRef, false);
        if (itemDef == null) {
            if (!unknownItems.includes(itemRef))
                unknownItems.push(itemRef);
            perQuestion.push({
                questionKey: key,
                itemRef,
                streamId: null,
                coveredBy: null,
                missing: false,
                unknownItem: true
            });
            continue;
        }
        const streamId = itemDef.data?.streamId ?? null;
        if (streamId == null) {
            // Item exists in the model but has no streamId — shouldn't happen for
            // real items, but be defensive: treat as a missing-but-unactionable row.
            perQuestion.push({
                questionKey: key,
                itemRef,
                streamId: null,
                coveredBy: null,
                missing: true,
                unknownItem: false
            });
            continue;
        }
        const coveredBy = findCoveringPermission(streamId);
        const missing = coveredBy == null;
        if (missing)
            missingItemKeysSet.add(itemRef);
        perQuestion.push({
            questionKey: key,
            itemRef,
            streamId,
            coveredBy,
            missing,
            unknownItem: false
        });
    }
    let proposedPermissions = [];
    if (missingItemKeysSet.size > 0) {
        // Reuse the same min-permission computation that CollectorRequest.buildPermissions uses.
        // Pass existing permissions as `preRequest` so the result merges cleanly with what's there.
        const preRequest = req.permissions
            .filter((p) => typeof p?.streamId === 'string')
            .map((p) => ({ streamId: p.streamId, defaultName: p.defaultName, level: p.level }));
        const merged = model.authorizations.forItemKeys(Array.from(missingItemKeysSet), { preRequest: preRequest });
        // Only return entries the request doesn't already have (same streamId + same level).
        proposedPermissions = merged.filter((p) => {
            const existing = existingByStreamId[p.streamId];
            if (!existing)
                return true;
            return existing.level !== p.level;
        });
    }
    return {
        ok: perQuestion.every((c) => !c.missing),
        perQuestion,
        unknownItems,
        proposedPermissions
    };
}
